import React, { useMemo } from 'react';
import { Line } from "react-chartjs-2";
import { formatTime, formatHHMM } from '../../utils/timeUtils';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

// Route Type をラベルに変換するヘルパー関数
const getRouteLabel = (routeType) => {
    switch (routeType) {
        case 2:
            return "鉄道";
        case 3:
            return "バス";
        default:
            return "その他";
    }
};



// キャッシュ用オブジェクト
const patternCache = {};
const createHatchPattern = (ctx, index) => {
    if (patternCache[index]) return patternCache[index];
    const size = 10 + index * 2;
    const angle = (index % 4) * Math.PI / 4;
    const patternCanvas = document.createElement('canvas');
    const patternContext = patternCanvas.getContext('2d');
    patternCanvas.width = size;
    patternCanvas.height = size;
    patternContext.fillStyle = 'white';
    patternContext.fillRect(0, 0, size, size);
    patternContext.strokeStyle = 'rgba(66, 66, 66, 0.8)';
    patternContext.lineWidth = 1;
    patternContext.save();
    patternContext.translate(size / 2, size / 2);
    patternContext.rotate(angle);
    patternContext.translate(-size / 2, -size / 2);
    for (let i = -size; i < size * 2; i += size / 4) {
        patternContext.beginPath();
        patternContext.moveTo(i, 0);
        patternContext.lineTo(i - size, size);
        patternContext.stroke();
    }
    patternContext.restore();
    const pattern = ctx.createPattern(patternCanvas, 'repeat');
    patternCache[index] = pattern;
    return pattern;
};

/** 折れ線グラフ */
const LineChartComponent = ({ lineData }) => {
    // 折れ線グラフ用データ処理をメモ化
    const processedData = useMemo(() => {
        const formattedData = {};
        lineData.forEach((curr) => {
            const formattedTime = formatHHMM(curr.timestamp);
            const key = `${curr.route_type}-${formattedTime}`;
            if (!formattedData[key]) {
                formattedData[key] = { route_type: curr.route_type, timestamp: formattedTime, count: 0, entries: 0 };
            }
            formattedData[key].count += curr.count;
            formattedData[key].entries += 1;
        });
        return Object.values(formattedData).map((item) => ({
            ...item,
            count: item.count / item.entries,
        }));
    }, [lineData]);

    const uniqueRouteTypes = [...new Set(processedData.map((d) => d.route_type))];
    const uniqueTimes = [...new Set(processedData.map((d) => d.timestamp))];

    const datasets = uniqueRouteTypes.map((route_type, index) => {
        const data = uniqueTimes.map((timestamp) => {
            const entry = processedData.find((d) => d.route_type === route_type && d.timestamp === timestamp);
            return entry ? entry.count : 0;
        });

        return {
            label: getRouteLabel(route_type),
            data,
            borderColor: 'rgba(45, 45, 45, 0.8)',
            borderWidth: 0.5,
            backgroundColor: (ctx) => createHatchPattern(ctx.chart.ctx, index),
            fill: true,
            tension: 0.4,
            pointRadius: 0,
        };
    });

    const chartData = {
        labels: uniqueTimes,
        datasets,
    };
    const fonts = '"ヒラギノ明朝 ProN", "Hiragino Mincho ProN", "Yu Mincho", "游明朝", "MS PMincho", "MS Mincho", serif';
    const options = {
        responsive: true, // 横方向レスポンシブ
        maintainAspectRatio: false, // アスペクト比を無効化
        
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    font: {
                        size: 14,
                        family: fonts,
                    },
                },
            },
            // title: {
            //     display: true,
            //     text: "観測菌 - 時系列変化",
            //     font: {
            //         size: 18,
            //         weight: 'normal', // 太字を外す
            //         family: fonts,
            //     },
            // },
            tooltip: {
                callbacks: {
                    label: (tooltipItem) => `Route: ${tooltipItem.dataset.label}, Count: ${tooltipItem.raw}`,
                },
            },
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: '時刻',
                    font: {
                        size: 14,
                        family: fonts,
                    },
                },
                ticks:{
                    font:{
                        family: fonts,
                    },
                    maxTicksLimit: 5, // 表示するラベルの最大数を指定
                },
            },
            y: {
                stacked: true,
                title: {
                    display: true,
                    text: '菌数',
                    font: {
                        size: 14,
                        family: fonts,
                    },
                },
                ticks:{
                    font:{
                        family: fonts,
                    }
                },
                beginAtZero: true,
            },
        },
    };

    return (
        <div style={{ height: '400px', width: '100%' }}> {/* 縦400px、横はレスポンシブ */}
            <Line data={chartData} options={options} />
        </div>
    );
};

export default LineChartComponent;
